import { useMemo } from "react";
import { useExtendedSearchQuery } from "src/api/client/extended-search.api";
import { IFilterTag } from "src/api/server/extended-search.types";
import { mapSearchQueryToSearchPayload } from "src/components/pages/search/utils/map-search-query-to-search-payload/map-search-query-to-search-payload";
import { ITableRanking } from "src/components-mui/modules/wikifolio-table/types";
import { IWikifolioTableFigure } from "src/components-mui/modules/wikifolio-table/wikifolio-table-module";

const DEFAULT_QUERY_PARAMS = "investable=true&sortOrder=asc&sortBy=topwikis";
const getUrlSearchParamsWithFallback = (query?: string) => {
    if (query?.includes("?")) {
        return new URLSearchParams(query.split("?").pop());
    }

    return new URLSearchParams(DEFAULT_QUERY_PARAMS);
};

interface IUseWikifolioTableModuleSearchParams {
    wikifolioIsins: string[];
    searchQuery?: string;
    defaultFigures?: IWikifolioTableFigure[];
    allRankings?: ITableRanking[];
    tags?: IFilterTag[];
    limit?: number;
}
export const useWikifolioTableModuleSearch = ({
    wikifolioIsins,
    searchQuery,
    defaultFigures = [],
    allRankings = [],
    tags = [],
    limit = 10,
}: IUseWikifolioTableModuleSearchParams) => {
    const defaultFiguresIds = useMemo(() => defaultFigures?.map(figure => figure.id) ?? [], [defaultFigures]);
    const searchParams = useMemo(() => getUrlSearchParamsWithFallback(searchQuery), [searchQuery]);
    const queryKey = useMemo(
        () =>
            wikifolioIsins && wikifolioIsins.length > 0
                ? `wikifolioIsins:${wikifolioIsins.join(",")};figures:${defaultFiguresIds.join(",")}`
                : `${searchQuery ?? ""};figures:${defaultFiguresIds.join(",")}`,
        [searchQuery, wikifolioIsins, defaultFiguresIds]
    );

    const searchPayload = useMemo(
        () =>
            wikifolioIsins && wikifolioIsins.length > 0
                ? { wikifolioIsins, selectedFigures: defaultFiguresIds, limit }
                : mapSearchQueryToSearchPayload(searchParams, allRankings, tags, true),
        [allRankings, defaultFiguresIds, limit, searchParams, tags, wikifolioIsins]
    );

    const { isLoading, data } = useExtendedSearchQuery({ ...searchPayload, selectedFigures: defaultFiguresIds, limit }, [queryKey]);

    return {
        isLoading,
        results: data?.results,
    };
};
